/**
 * @file nav-items.ts
 * @description Shared navigation item definitions used by both the Sidebar and
 *              the header's CommandSearch palette. Add / remove modules here to
 *              update both at once.
 */

import { EXTERNAL_LINKS, ROUTES } from '@/utils/constants';
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  Database,
  Box,
  Boxes,
  Code,
  ListChecks,
  TableProperties,
  Building2,
  Building,
  GraduationCap,
  Users,
  User,
  ShieldCheck,
  UserCog,
  FileText,
  Image,
  Megaphone,
  Info,
  CreditCard,
  Receipt,
  Coins,
  BarChart3,
  BarChart4,
  TrendingUp,
  FileBadge,
  Cloud,
  Wrench,
  UserCheck,
  Ticket,
  SlidersHorizontal,
  Tags,
  Zap,
  SmartphoneNfc,
  Clock,
  Layout,
  BrainCircuit,
  ClipboardList,
  Trophy,
  Dumbbell,
  Ruler,
  Layers,
  UsersRound,
  Files,
  Variable,
  KeyRound,
  Store,
  MessageSquarePlus,
  Smartphone,
  BadgeCheck,
  Award,
  FileSpreadsheet,
  Calculator,
  CalendarDays,
  ReceiptText,
  BookOpen,
  Wallet,
  Sparkles,
  Network,
  Scale,
  ArrowLeftRight,
} from 'lucide-react';

export interface NavItem {
  label: string;
  path?: string;
  /** Absolute URL — rendered as an external link that opens in a new tab */
  externalUrl?: string;
  icon: LucideIcon;
  children?: NavItem[];
  groupKey?: string;
  /** Parent group label — auto-populated by flattenNavItems */
  group?: string;
  isPrivate?: boolean;
}

/**
 * Single source of truth for sidebar navigation.
 * Import this in SidebarNav AND in CommandSearch.
 */
export const NAV_ITEMS: NavItem[] = [
  // {
  //   label: 'Kipi School & Institute',
  //   groupKey: 'group-kipi-school-institute',
  //   icon: Building2,
  //   children: [
  //     {
  //       label: 'Dashboard',
  //       path: ROUTES.DASHBOARD,
  //       icon: LayoutDashboard,
  //     },
  //     // {
  //     //   label: 'Subscription Dashboard',
  //     //   groupKey: 'sub-subscription-dashboard',
  //     //   icon: PieChart,
  //     //   isPrivate: true,
  //     //   children: [
  //     //     {
  //     //       label: 'Subscription Plan Usage',
  //     //       path: ROUTES.SUBSCRIPTION_DASHBOARD,
  //     //       icon: BarChart4,
  //     //     },
  //     //     {
  //     //       label: 'Subscription Analysis',
  //     //       path: ROUTES.SUBSCRIPTION_ANALYSIS,
  //     //       icon: TrendingUp,
  //     //     },
  //     //   ],
  //     // },
  //     {
  //       label: 'App Analytics',
  //       groupKey: 'sub-analytics',
  //       icon: BarChart3,
  //       children: [
  //         // { label: 'Dashboard', path: ROUTES.ANALYTICS_DASHBOARD, icon: LayoutDashboard },
  //         // { label: 'Engagement', path: ROUTES.ANALYTICS_ENGAGEMENT, icon: Zap },
  //         // { label: 'Version', path: ROUTES.ANALYTICS_VERSION, icon: SmartphoneNfc },
  //         // { label: 'Pattern', path: ROUTES.ANALYTICS_PATTERN, icon: Clock },
  //       ],
  //     },
  //     {
  //       label: 'Master Management',
  //       groupKey: 'sub-master',
  //       icon: Database,
  //       isPrivate: true,
  //       children: [
  //         // { label: 'Entity', path: ROUTES.ENTITY, icon: Box },
  //         // { label: 'Entity Type', path: ROUTES.ENTITYTYPE, icon: Boxes },
  //         // { label: 'Type Management', path: ROUTES.TYPE_MANAGEMENT, icon: ListChecks },
  //         { label: 'Sheet Fields', path: ROUTES.SHEET_FIELD, icon: TableProperties },
  //         { label: 'Designation', path: ROUTES.DESIGNATION, icon: UserCheck },
  //         { label: 'Dynamic Variable', path: ROUTES.DYNAMIC_VARIABLE, icon: SlidersHorizontal },
  //         { label: 'Dynamic Variable Label', path: ROUTES.DYNAMIC_VARIABLE_LABEL, icon: Tags },
  //       ],
  //     },
  //     {
  //       label: 'Institute & Schools',
  //       groupKey: 'sub-schools',
  //       icon: Building2,
  //       children: [
  //         { label: 'Institute List', path: ROUTES.INSTITUTE_LIST, icon: Building },
  //         { label: 'School List', path: ROUTES.SCHOOL_LIST, icon: GraduationCap },
  //       ],
  //     },
  //     {
  //       label: 'User & Access',
  //       groupKey: 'sub-users',
  //       icon: Users,
  //       children: [
  //         { label: 'Teachers', path: ROUTES.TEACHERS_LIST, icon: User },
  //         { label: 'Students', path: ROUTES.STUDENTS_LIST, icon: User },
  //         { label: 'Parents', path: ROUTES.PARENTS_LIST, icon: User },
  //         { label: 'Institute Admins', path: ROUTES.INSTITUTE_ADMINS_LIST, icon: User },
  //         {
  //           label: 'Institute Master Admins',
  //           path: ROUTES.INSTITUTE_MASTER_ADMINS_LIST,
  //           icon: User,
  //         },
  //         { label: 'Admins', path: ROUTES.ADMINS_LIST, icon: User },
  //         {
  //           label: 'Module Permissions',
  //           path: ROUTES.PERMISSIONS_LIST,
  //           icon: Code,
  //           isPrivate: true,
  //         },
  //         {
  //           label: 'Roles & Permission',
  //           path: ROUTES.ROLE_MANAGEMENT_LIST,
  //           icon: ShieldCheck,
  //           isPrivate: true,
  //         },
  //         { label: 'Sub Admins', path: ROUTES.SUB_ADMINS, icon: UserCog, isPrivate: true },
  //         {
  //           label: 'Education Officers',
  //           path: ROUTES.EDUCATION_OFFICER,
  //           icon: UserCheck,
  //           isPrivate: true,
  //         },
  //       ],
  //     },
  //     {
  //       label: 'Content Management',
  //       groupKey: 'sub-content',
  //       icon: FileText,
  //       children: [
  //         { label: 'Banners', path: ROUTES.BANNER, icon: Image },
  //         { label: 'Announcements', path: ROUTES.ANNOUNCEMENT, icon: Megaphone },
  //         { label: 'Advertisements', path: ROUTES.ADVERTISEMENT, icon: Layout },
  //         { label: 'Information', path: ROUTES.INFORMATION, icon: Info },
  //         // { label: 'Sample Files', path: ROUTES.SAMPLE_FILES, icon: File },
  //         { label: 'Terms & Conditions', path: ROUTES.TERMS_AND_CONDITION, icon: FileText },
  //         // { label: 'Community Group', path: ROUTES.COMMUNITY_GROUP, icon: MessagesSquare },
  //       ],
  //     },
  //     // {
  //     //   label: 'Subscription & Billing',
  //     //   groupKey: 'sub-billing',
  //     //   icon: CreditCard,
  //     //   isPrivate: true,
  //     //   children: [
  //     //     // { label: 'Subscription Plans', path: ROUTES.VIEW_SUBSCRIPTION_PLAN, icon: Receipt },
  //     //     // { label: 'Module Price', path: ROUTES.MODULE_PRICE, icon: Tags },
  //     //     // { label: 'Coin Offers', path: ROUTES.COIN_OFFER, icon: Coins },
  //     //     // { label: 'Coin History', path: ROUTES.COIN_HISTORY, icon: Coins },
  //     //     // { label: 'Cloud Storage', path: ROUTES.CLOUD_STORAGE, icon: Cloud },
  //     //     // { label: 'Storage History', path: ROUTES.STORAGE_HISTORY, icon: Cloud },
  //     //     // {
  //     //     //   label: 'Subscription Configuration',
  //     //     //   path: ROUTES.SUBSCRIPTION_CONFIGURATION,
  //     //     //   icon: SlidersHorizontal,
  //     //     // },
  //     //   ],
  //     // },
  //     {
  //       label: 'Reports & Certificates',
  //       groupKey: 'sub-reports',
  //       icon: BarChart3,
  //       children: [
  //         { label: 'Report Columns', path: ROUTES.REPORT_COLUMNS, icon: BarChart4 },
  //         { label: 'Certificates', path: ROUTES.CERTIFICATES_MANAGEMENT, icon: FileBadge },
  //       ],
  //     },
  //     // {
  //     //   label: 'E-Learning',
  //     //   path: ROUTES.E_LEARNING,
  //     //   icon: BookOpen,
  //     // },
  //     // {
  //     //   label: 'System Settings',
  //     //   groupKey: 'sub-settings',
  //     //   icon: Settings,
  //     //   children: [{ label: 'Maintenance', path: ROUTES.MAINTENANCE, icon: Wrench }],
  //     // },
  //     // {
  //     //   label: 'Tickets',
  //     //   path: ROUTES.TICKET,
  //     //   icon: Ticket,
  //     // },
  //     // {
  //     //   label: 'Kipiverse Inquiries',
  //     //   path: ROUTES.KIPIVERSE_INQUIRYS,
  //     //   icon: MessagesSquare,
  //     // },
  //     // {
  //     //   label: 'Module Doc AI',
  //     //   path: ROUTES.MODULE_DOC_AI,
  //     //   icon: BrainCircuit,
  //     //   isPrivate: true,
  //     // },
  //     // {
  //     //   label: 'Application Management',
  //     //   path: ROUTES.APPLICATION_MANAGEMENT,
  //     //   icon: SmartphoneNfc,
  //     //   isPrivate: true,
  //     // },
  //   ],
  // },
  // {
  //   label: 'Kipi Marketplace',
  //   groupKey: 'group-kipi-marketplace',
  //   icon: Boxes,
  //   children: [
  //     {
  //       label: 'Add Category',
  //       path: ROUTES.MARKETPLACE_ADD_CATEGORY,
  //       icon: Tags,
  //       isPrivate: true,
  //     },
  //     {
  //       label: 'Company Approval Req',
  //       path: ROUTES.MARKETPLACE_COMPANY_APPROVAL,
  //       icon: ShieldCheck,
  //       isPrivate: true,
  //     },
  //   ],
  // },
  // {
  //   label: 'Kipi Arena',
  //   groupKey: 'group-kipi-arena',
  //   icon: Trophy,
  //   children: [
  //     {
  //       label: 'Sports',
  //       path: ROUTES.ARENA_SPORTS,
  //       icon: Trophy,
  //     },
  //     {
  //       label: 'Sport Categories',
  //       path: ROUTES.ARENA_SPORT_CATEGORIES,
  //       icon: Tags,
  //     },
  //     {
  //       label: 'Sport Sub Categories',
  //       path: ROUTES.ARENA_SPORT_SUB_CATEGORIES,
  //       icon: Boxes,
  //     },
  //     {
  //       label: 'Sport Roles',
  //       path: ROUTES.ARENA_SPORT_ROLES,
  //       icon: UserCog,
  //     },
  //     {
  //       label: 'Equipments',
  //       path: ROUTES.ARENA_EQUIPMENTS,
  //       icon: Dumbbell,
  //     },
  //     {
  //       label: 'Facility Masters',
  //       path: ROUTES.ARENA_FACILITY_MASTERS,
  //       icon: Building2,
  //     },
  //     {
  //       label: 'Units',
  //       path: ROUTES.ARENA_UNITS,
  //       icon: Ruler,
  //     },
  //     {
  //       label: 'Skills',
  //       path: ROUTES.ARENA_SKILLS,
  //       icon: Zap,
  //     },
  //     {
  //       label: 'Age Groups',
  //       path: ROUTES.ARENA_AGE_GROUPS,
  //       icon: Users,
  //     },
  //     {
  //       label: 'Sport Group Data',
  //       path: ROUTES.ARENA_SPORT_GROUP_DATA,
  //       icon: Layers,
  //     },
  //     {
  //       label: 'Assessment Tests',
  //       path: ROUTES.ARENA_ASSESSMENT_TESTS,
  //       icon: ClipboardList,
  //     },
  //     {
  //       label: 'Sport Infos',
  //       path: ROUTES.ARENA_SPORT_INFOS,
  //       icon: Info,
  //     },
  //   ],
  // },
  // ------------------------------------------------------------------------

  {
    label: 'Dashboard',
    path: ROUTES.DASHBOARD,
    isPrivate: true,
    icon: LayoutDashboard,
  },
  {
    label: 'FRC Docs',
    path: ROUTES.FRC_DOCS,
    icon: FileText,
  },
  {
    label: 'Accounting Configuration',
    groupKey: 'group-accounting-configuration',
    // isPrivate: true,
    icon: Calculator,
    children: [
      {
        label: 'Financial Years',
        path: ROUTES.ACCOUNTING_FINANCIAL_YEARS,
        icon: CalendarDays,
        // isPrivate: true,
      },
      {
        label: 'Voucher Types',
        path: ROUTES.ACCOUNTING_VOUCHER_TYPES,
        icon: ReceiptText,
        // isPrivate: true,
      },
      {
        label: 'Account Groups',
        path: ROUTES.ACCOUNTING_ACCOUNT_GROUPS,
        icon: BookOpen,
        // isPrivate: true,
      },
      {
        label: 'Account Ledgers',
        path: ROUTES.ACCOUNTING_ACCOUNT_LEDGERS,
        icon: Wallet,
        // isPrivate: true,
      },
      {
        label: 'Journal Mappings',
        path: ROUTES.ACCOUNTING_JOURNAL_MAPPINGS,
        icon: ArrowLeftRight,
        // isPrivate: true,
      },
      {
        label: 'Cost Centers',
        path: ROUTES.ACCOUNTING_COST_CENTERS,
        icon: Building2,
        // isPrivate: true,
      },
    ],

  },
    {
    label: 'TEPDS',
    groupKey: 'group-tepds',
    // isPrivate: true,
    icon: Award,
    children: [
      {
        label: 'Category',
        path: ROUTES.TEPDS_CATEGORY,
        icon: Tags,
        // isPrivate: true,
      },
      {
        label: 'Rating Scales',
        path: ROUTES.TEPDS_RATING_SCALES,
        icon: SlidersHorizontal,
        // isPrivate: true,
      },
      {
        label: 'Templates',
        path: ROUTES.TEPDS_TEMPLATES,
        icon: FileSpreadsheet,
        // isPrivate: true,
      },
      {
        label: 'Template Map',
        path: ROUTES.TEPDS_TEMPLATE_MAPS,
        icon: Network,
      },
      {
        label: 'Evaluation Weight Configurations',
        path: ROUTES.TEPDS_WEIGHT_CONFIGS,
        icon: Scale,
      },
    ],
  },
  {
    label: 'Kipi School & Institute',
    groupKey: 'group-kipi-school-institute',
    icon: Building2,
    children: [
      // {
      //   label: 'Dashboard',
      //   path: ROUTES.DASHBOARD,
      //   icon: LayoutDashboard,
      // },
      // {
      //   label: 'Subscription Dashboard',
      //   groupKey: 'sub-subscription-dashboard',
      //   icon: PieChart,
      //   isPrivate: true,
      //   children: [
      //     {
      //       label: 'Subscription Plan Usage',
      //       path: ROUTES.SUBSCRIPTION_DASHBOARD,
      //       icon: BarChart4,
      //     },
      //     {
      //       label: 'Subscription Analysis',
      //       path: ROUTES.SUBSCRIPTION_ANALYSIS,
      //       icon: TrendingUp,
      //     },
      //   ],
      // },
      // {
      //   label: 'App Analytics',
      //   groupKey: 'sub-analytics',
      //   icon: BarChart3,
      //   children: [
      //     // { label: 'Dashboard', path: ROUTES.ANALYTICS_DASHBOARD, icon: LayoutDashboard },
      //     // { label: 'Engagement', path: ROUTES.ANALYTICS_ENGAGEMENT, icon: Zap },
      //     // { label: 'Version', path: ROUTES.ANALYTICS_VERSION, icon: SmartphoneNfc },
      //     // { label: 'Pattern', path: ROUTES.ANALYTICS_PATTERN, icon: Clock },
      //   ],
      // },
      {
        label: 'Master Management',
        groupKey: 'sub-master',
        icon: Database,
        isPrivate: true,
        children: [
          // { label: 'Entity', path: ROUTES.ENTITY, icon: Box },
          // { label: 'Entity Type', path: ROUTES.ENTITYTYPE, icon: Boxes },
          // { label: 'Type Management', path: ROUTES.TYPE_MANAGEMENT, icon: ListChecks },
          { label: 'Sheet Fields', path: ROUTES.SHEET_FIELD, icon: TableProperties },
          { label: 'Designation', path: ROUTES.DESIGNATION, icon: UserCheck },
          { label: 'New Designation', path: ROUTES.NEW_DESIGNATION, icon: UserCheck },
          { label: 'Dynamic Variable', path: ROUTES.DYNAMIC_VARIABLE, icon: SlidersHorizontal },
          { label: 'Dynamic Variable Label', path: ROUTES.DYNAMIC_VARIABLE_LABEL, icon: Tags },
        ],
      },
      {
        label: 'Institute & Schools',
        groupKey: 'sub-schools',
        icon: Building2,
        children: [
          { label: 'Institute List', path: ROUTES.INSTITUTE_LIST, icon: Building },
          { label: 'School List', path: ROUTES.SCHOOL_LIST, icon: GraduationCap },
        ],
      },
   
      {
        label: 'User & Access',
        groupKey: 'sub-users',
        icon: Users,
        children: [
          // { label: 'Teachers', path: ROUTES.TEACHERS_LIST, icon: User },
          // { label: 'Students', path: ROUTES.STUDENTS_LIST, icon: User },
          // { label: 'Parents', path: ROUTES.PARENTS_LIST, icon: User },
          // { label: 'Institute Admins', path: ROUTES.INSTITUTE_ADMINS_LIST, icon: User },
          // {
          //   label: 'Institute Master Admins',
          //   path: ROUTES.INSTITUTE_MASTER_ADMINS_LIST,
          //   icon: User,
          // },
          // { label: 'Admins', path: ROUTES.ADMINS_LIST, icon: User },
          // {
          //   label: 'Module Permissions',
          //   path: ROUTES.PERMISSIONS_LIST,
          //   icon: Code,
          //   isPrivate: true,
          // },
          // {
          //   label: 'Roles & Permission',
          //   path: ROUTES.ROLE_MANAGEMENT_LIST,
          //   icon: ShieldCheck,
          //   isPrivate: true,
          // },
          // { label: 'Sub Admins', path: ROUTES.SUB_ADMINS, icon: UserCog, isPrivate: true },
          {
            label: 'Education Officers',
            path: ROUTES.EDUCATION_OFFICER,
            icon: UserCheck,
            isPrivate: true,
          },
          {
            label: 'DEO/DPEO',
            path: ROUTES.DEO_DPEO,
            icon: ShieldCheck,
          },
        ],
      },
      {
        label: 'Roles & Permissions',
        groupKey: 'group-roles-permissions',
        isPrivate: true,
        icon: KeyRound,
        children: [
          {
            label: 'Module Permissions',
            path: ROUTES.PERMISSIONS_LIST,
            icon: Code,
            isPrivate: true,
          },
          {
            label: 'Roles & Permission',
            path: ROUTES.ROLE_MANAGEMENT_LIST,
            icon: ShieldCheck,
            isPrivate: true,
          },
        ],
      },
      // {
      //   label: 'Content Management',
      //   groupKey: 'sub-content',
      //   icon: FileText,
      //   children: [
      //     // { label: 'Banners', path: ROUTES.BANNER, icon: Image },
      //     // { label: 'Announcements', path: ROUTES.ANNOUNCEMENT, icon: Megaphone },
      //     // { label: 'Advertisements', path: ROUTES.ADVERTISEMENT, icon: Layout },
      //     // { label: 'Information', path: ROUTES.INFORMATION, icon: Info },
      //     // { label: 'Sample Files', path: ROUTES.SAMPLE_FILES, icon: File },
      //     // { label: 'Terms & Conditions', path: ROUTES.TERMS_AND_CONDITION, icon: FileText },
      //     // { label: 'Community Group', path: ROUTES.COMMUNITY_GROUP, icon: MessagesSquare },
      //   ],
      // },
      // {
      //   label: 'Subscription & Billing',
      //   groupKey: 'sub-billing',
      //   icon: CreditCard,
      //   isPrivate: true,
      //   children: [
      //     // { label: 'Subscription Plans', path: ROUTES.VIEW_SUBSCRIPTION_PLAN, icon: Receipt },
      //     // { label: 'Module Price', path: ROUTES.MODULE_PRICE, icon: Tags },
      //     // { label: 'Coin Offers', path: ROUTES.COIN_OFFER, icon: Coins },
      //     // { label: 'Coin History', path: ROUTES.COIN_HISTORY, icon: Coins },
      //     // { label: 'Cloud Storage', path: ROUTES.CLOUD_STORAGE, icon: Cloud },
      //     // { label: 'Storage History', path: ROUTES.STORAGE_HISTORY, icon: Cloud },
      //     // {
      //     //   label: 'Subscription Configuration',
      //     //   path: ROUTES.SUBSCRIPTION_CONFIGURATION,
      //     //   icon: SlidersHorizontal,
      //     // },
      //   ],
      // },
      // {
      //   label: 'Reports & Certificates',
      //   groupKey: 'sub-reports',
      //   icon: BarChart3,
      //   children: [
      //     { label: 'Report Columns', path: ROUTES.REPORT_COLUMNS, icon: BarChart4 },
      //     { label: 'Certificates', path: ROUTES.CERTIFICATES_MANAGEMENT, icon: FileBadge },
      //   ],
      // },
      // {
      //   label: 'E-Learning',
      //   path: ROUTES.E_LEARNING,
      //   icon: BookOpen,
      // },
      // {
      //   label: 'System Settings',
      //   groupKey: 'sub-settings',
      //   icon: Settings,
      //   children: [{ label: 'Maintenance', path: ROUTES.MAINTENANCE, icon: Wrench }],
      // },
      // {
      //   label: 'Tickets',
      //   path: ROUTES.TICKET,
      //   icon: Ticket,
      // },
      // {
      //   label: 'Kipiverse Inquiries',
      //   path: ROUTES.KIPIVERSE_INQUIRYS,
      //   icon: MessagesSquare,
      // },
      // {
      //   label: 'Module Doc AI',
      //   path: ROUTES.MODULE_DOC_AI,
      //   icon: BrainCircuit,
      //   isPrivate: true,
      // },
      // {
      //   label: 'Application Management',
      //   path: ROUTES.APPLICATION_MANAGEMENT,
      //   icon: SmartphoneNfc,
      //   isPrivate: true,
      // },
    ],
  },
     {
        label: 'Approvals',
        groupKey: 'sub-approvals',
        icon: BadgeCheck,
        children: [
          { label: 'School', path: ROUTES.APPROVAL_SCHOOLS, icon: GraduationCap },
          { label: 'Institute', path: ROUTES.APPROVAL_INSTITUTES, icon: Building },
          // { label: 'College', path: ROUTES.APPROVAL_COLLEGES, icon: School },
          { label: 'Teacher', path: ROUTES.APPROVAL_TEACHERS, icon: UserCheck },
          { label: 'Student', path: ROUTES.APPROVAL_STUDENTS, icon: User },
          { label: 'Sports Academy', path: ROUTES.APPROVAL_SPORTS_ACADEMIES, icon: Trophy },
          { label: 'Trainer', path: ROUTES.APPROVAL_TRAINERS, icon: Dumbbell },
          { label: 'Athleet', path: ROUTES.APPROVAL_ATHLETES, icon: Award },
          { label: 'Company Approval Req', path: ROUTES.MARKETPLACE_COMPANY_APPROVAL, icon: Building2 },
        ],
      },
  { label: 'Master Setting', path: ROUTES.MASTER_SETTINGS, icon: SlidersHorizontal },

  {
    label: 'Super Admin',
    groupKey: 'group-super-admin',
    isPrivate: true,
    icon: ShieldCheck,
    children: [{ label: 'Sub Admins', path: ROUTES.SUB_ADMINS, icon: UserCog, isPrivate: true }],
  },

  {
    label: 'Subscription Plans',
    groupKey: 'group-subscription-plans',
    isPrivate: true,
    icon: CreditCard,
    children: [
      { label: 'Subscription Plans', path: ROUTES.VIEW_SUBSCRIPTION_PLAN, icon: Receipt },
      { label: 'Module Price', path: ROUTES.MODULE_PRICE, icon: Tags },
      {
        label: 'Subscription Plan Usage',
        path: ROUTES.SUBSCRIPTION_DASHBOARD,
        icon: BarChart4,
      },
      {
        label: 'Subscription Analysis',
        path: ROUTES.SUBSCRIPTION_ANALYSIS,
        icon: TrendingUp,
      },
    ],
  },
  {
    label: 'Storage & Credits',
    groupKey: 'group-storage-credits',
    isPrivate: true,
    icon: Database,
    children: [
      { label: 'Coin Offers', path: ROUTES.COIN_OFFER, icon: Coins },
      { label: 'Coin History', path: ROUTES.COIN_HISTORY, icon: Coins },
      { label: 'Cloud Storage', path: ROUTES.CLOUD_STORAGE, icon: Cloud },
      { label: 'Storage History', path: ROUTES.STORAGE_HISTORY, icon: Cloud },
      {
        label: 'Subscription Configuration',
        path: ROUTES.SUBSCRIPTION_CONFIGURATION,
        icon: SlidersHorizontal,
      },
    ],
  },

  {
    label: 'User Types & Users',
    groupKey: 'group-user-types-users',
    isPrivate: true,
    icon: Users,
    children: [
      { label: 'Teachers', path: ROUTES.TEACHERS_LIST, icon: User },
      { label: 'Students', path: ROUTES.STUDENTS_LIST, icon: User },
      { label: 'Parents', path: ROUTES.PARENTS_LIST, icon: User },
      { label: 'Institute Admins', path: ROUTES.INSTITUTE_ADMINS_LIST, icon: User },
      {
        label: 'Institute Master Admins',
        path: ROUTES.INSTITUTE_MASTER_ADMINS_LIST,
        icon: User,
      },
    ],
  },
  {
    label: 'App Analytics',
    groupKey: 'group-app-analytics',
    isPrivate: true,
    icon: BarChart3,
    children: [
      { label: 'Dashboard', path: ROUTES.ANALYTICS_DASHBOARD, icon: LayoutDashboard },
      { label: 'Engagement', path: ROUTES.ANALYTICS_ENGAGEMENT, icon: Zap },
      { label: 'Version', path: ROUTES.ANALYTICS_VERSION, icon: SmartphoneNfc },
      { label: 'Pattern', path: ROUTES.ANALYTICS_PATTERN, icon: Clock },
    ],
  },
  {
    label: 'Complaint & Tickets',
    isPrivate: true,
    icon: Ticket,
    path: ROUTES.TICKET,
  },
  {
    label: 'Approval Dashboard',
    groupKey: 'group-approval-dashboard',
    isPrivate: true,
    icon: BadgeCheck,
    children: [],
  },
  {
    label: 'Maintenance Settings',
    path: ROUTES.MAINTENANCE,
    isPrivate: true,
    icon: Wrench,
  },
  {
    label: 'Demo App Management',
    path: ROUTES.APPLICATION_MANAGEMENT,
    isPrivate: true,
    icon: Smartphone,
  },
  {
    label: 'AI Datasets',
    path: ROUTES.MODULE_DOC_AI,
    isPrivate: true,
    icon: BrainCircuit,
  },
  {
    label: 'New Inquiry',
    path: ROUTES.KIPIVERSE_INQUIRYS,
    isPrivate: true,
    icon: MessageSquarePlus,
  },
  {
    label: 'Kipi Marketplace',
    groupKey: 'group-kipi-marketplace',
    isPrivate: true,
    icon: Store,
    children: [
      {
        label: 'Add Category',
        path: ROUTES.MARKETPLACE_ADD_CATEGORY,
        icon: Tags,
        isPrivate: true,
      },
      {
        label: 'Attribute',
        path: ROUTES.MARKETPLACE_ATTRIBUTE,
        icon: SlidersHorizontal,
        isPrivate: true,
      },
      {
        label: 'Company Approval Req',
        path: ROUTES.MARKETPLACE_COMPANY_APPROVAL,
        icon: ShieldCheck,
        isPrivate: true,
      },
    ],
  },
  {
    label: 'Kipi Arena',
    groupKey: 'group-kipi-arena',
    isPrivate: true,
    icon: Trophy,
    children: [
      {
        label: 'Sports',
        path: ROUTES.ARENA_SPORTS,
        icon: Trophy,
      },
      {
        label: 'Sport Categories',
        path: ROUTES.ARENA_SPORT_CATEGORIES,
        icon: Tags,
      },
      {
        label: 'Sport Sub Categories',
        path: ROUTES.ARENA_SPORT_SUB_CATEGORIES,
        icon: Boxes,
      },
      {
        label: 'Sport Roles',
        path: ROUTES.ARENA_SPORT_ROLES,
        icon: UserCog,
      },
      {
        label: 'Equipments',
        path: ROUTES.ARENA_EQUIPMENTS,
        icon: Dumbbell,
      },
      {
        label: 'Facility Masters',
        path: ROUTES.ARENA_FACILITY_MASTERS,
        icon: Building2,
      },
      {
        label: 'Facility Amenities',
        path: ROUTES.ARENA_FACILITY_AMENITIES,
        icon: Building2,
      },
      {
        label: 'Units',
        path: ROUTES.ARENA_UNITS,
        icon: Ruler,
      },
      {
        label: 'Skills',
        path: ROUTES.ARENA_SKILLS,
        icon: Zap,
      },
      {
        label: 'Age Groups',
        path: ROUTES.ARENA_AGE_GROUPS,
        icon: Users,
      },
      {
        label: 'Sport Group Data',
        path: ROUTES.ARENA_SPORT_GROUP_DATA,
        icon: Layers,
      },
      {
        label: 'Assessment Tests',
        path: ROUTES.ARENA_ASSESSMENT_TESTS,
        icon: ClipboardList,
      },
      {
        label: 'Sport Infos',
        path: ROUTES.ARENA_SPORT_INFOS,
        icon: Info,
      },
    ],
  },
  {
    label: 'E-Learning Courses',
    path: ROUTES.E_LEARNING,
    isPrivate: true,
    icon: GraduationCap,
  },
  {
    label: 'Roles & Permissions Global',
    groupKey: 'group-roles-permissions-global',
    isPrivate: true,
    icon: KeyRound,
    children: [
      {
        label: 'Module Permissions',
        path: ROUTES.GLOBAL_PERMISSIONS_LIST,
        icon: Code,
        isPrivate: true,
      },
      {
        label: 'Roles & Permission',
        path: ROUTES.GLOBAL_ROLE_MANAGEMENT_LIST,
        icon: ShieldCheck,
        isPrivate: true,
      },
    ],
  },
  {
    label: 'Type Management',
    path: ROUTES.TYPE_MANAGEMENT,
    isPrivate: true,
    icon: ListChecks,
  },
  {
    label: 'Variables',
    groupKey: 'group-variables',
    isPrivate: true,
    icon: Variable,
    children: [],
  },
  {
    label: 'Informatics',
    path: ROUTES.INFORMATION,
    isPrivate: true,
    icon: Info,
  },
  {
    label: 'Sample Files',
    path: ROUTES.SAMPLE_FILES,
    isPrivate: true,
    icon: Files,
  },
  {
    label: 'Concern Letter Formats',
    path: ROUTES.TERMS_AND_CONDITION,
    isPrivate: true,
    icon: FileText,
  },
  {
    label: 'Communities',
    path: ROUTES.COMMUNITY_GROUP,
    isPrivate: true,
    icon: UsersRound,
  },
  {
    label: 'Marketing',
    groupKey: 'group-marketing',
    isPrivate: true,
    icon: Megaphone,
    children: [
      { label: 'Banners', path: ROUTES.BANNER, icon: Image },
      { label: 'Announcements', path: ROUTES.ANNOUNCEMENT, icon: Megaphone },
      { label: 'Advertisements', path: ROUTES.ADVERTISEMENT, icon: Layout },
    ],
  },
  {
    label: 'Reports & Certificates',
    groupKey: 'group-reports-certificates',
    isPrivate: true,
    icon: FileBadge,
    children: [
      { label: 'Report Columns', path: ROUTES.REPORT_COLUMNS, icon: BarChart4 },
      { label: 'Certificates', path: ROUTES.CERTIFICATES_MANAGEMENT, icon: FileBadge },
      { label: 'Custom Certificates', path: ROUTES.CUSTOM_CERTIFICATES, icon: Award },
    ],
  },
  {
    label: 'Entities',
    groupKey: 'group-entities',
    isPrivate: true,
    icon: Boxes,
    children: [
      { label: 'Entity', path: ROUTES.ENTITY, icon: Box },
      { label: 'Entity Type', path: ROUTES.ENTITYTYPE, icon: Boxes },
    ],
  },
  {
    label: 'AI',
    externalUrl: EXTERNAL_LINKS.AI_ADMIN,
    icon: Sparkles,
  },

];

/**
 * Returns a flat list of all navigable pages for the search palette.
 * Groups themselves are NOT included — only leaf items with a path.
 * Traverses recursively to handle multi-level nested sidebars.
 */
export const getFlatNavItems = (): NavItem[] => {
  const result: NavItem[] = [];
  const recurse = (item: NavItem, parentLabel?: string, parentIsPrivate = false) => {
    const isPrivate = Boolean(item.isPrivate || parentIsPrivate);
    if (item.path) {
      result.push({ ...item, group: parentLabel || item.label, isPrivate });
    } else if (item.children) {
      item.children.forEach((child) => {
        recurse(child, parentLabel || item.label, isPrivate);
      });
    }
  };
  NAV_ITEMS.forEach((item) => recurse(item));
  return result;
};
